import { createBrowserRouter, Navigate } from "react-router-dom";

import { AppShell } from "@/components/layout/AppShell";
import { Permission } from "@/lib/permissions";
import { AuditTrailPage } from "@/pages/admin/AuditTrailPage";
import { BaysPage } from "@/pages/admin/BaysPage";
import { CollectionsPage } from "@/pages/admin/CollectionsPage";
import { CsePage } from "@/pages/admin/CsePage";
import { ContactsPage } from "@/pages/admin/ContactsPage";
import { CustomersPage } from "@/pages/admin/CustomersPage";
import { GroupsPage } from "@/pages/admin/GroupsPage";
import { HauliersPage } from "@/pages/admin/HauliersPage";
import { ImportsPage } from "@/pages/admin/ImportsPage";
import { InvoicesPage } from "@/pages/admin/InvoicesPage";
import { LanguagesPage } from "@/pages/admin/LanguagesPage";
import { LoadsPage } from "@/pages/admin/LoadsPage";
import { LoadPlannerPage } from "@/pages/admin/LoadPlannerPage";
import { ContainerLoadPlannerPage } from "@/pages/admin/ContainerLoadPlannerPage";
import { VehicleTypesPage } from "@/pages/admin/VehicleTypesPage";
import { RoutePlannerPage } from "@/pages/admin/RoutePlannerPage";
import { NotificationRulesPage } from "@/pages/admin/NotificationRulesPage";
import { NotificationTemplatesPage } from "@/pages/admin/NotificationTemplatesPage";
import { NotificationsPage } from "@/pages/admin/NotificationsPage";
import { PermissionsPage } from "@/pages/admin/PermissionsPage";
import { RateCardsPage } from "@/pages/admin/RateCardsPage";
import { ReportsPage } from "@/pages/admin/ReportsPage";
import { SettingsPage } from "@/pages/admin/SettingsPage";
import { SitesPage } from "@/pages/admin/SitesPage";
import { SlotApprovalsPage } from "@/pages/admin/SlotApprovalsPage";
import { SummaryPage } from "@/pages/admin/SummaryPage";
import { TranslationsPage } from "@/pages/admin/TranslationsPage";
import { UsersPage } from "@/pages/admin/UsersPage";
import { LoginPage } from "@/pages/auth/LoginPage";
import { ProfilePage } from "@/pages/auth/ProfilePage";
import { DepotDashboardPage } from "@/pages/depot/DepotDashboardPage";
import { HaulierDashboardPage } from "@/pages/haulier/HaulierDashboardPage";
import { LoadboardPage } from "@/pages/loadboard/LoadboardPage";
import { ForbiddenPage } from "@/pages/public/ForbiddenPage";
import { LandingPage } from "@/pages/public/LandingPage";
import { NotFoundPage } from "@/pages/public/NotFoundPage";
import { RequireAuth, RequirePermission } from "./guards";

/**
 * The permission-guarded route table (README 0.5.4). Anonymous visitors only
 * reach `/` and `/login`; every other route sits behind RequireAuth and is
 * further gated by RequirePermission.
 */
export const router = createBrowserRouter([
  { path: "/", element: <LandingPage /> },
  { path: "/login", element: <LoginPage /> },
  {
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          { path: "/profile", element: <ProfilePage /> },
          { path: "/403", element: <ForbiddenPage /> },
          {
            element: (
              <RequirePermission
                permissions={[Permission.LOADS_READ, Permission.LOADBOARD_READ]}
              />
            ),
            children: [{ path: "/loadboard", element: <LoadboardPage /> }],
          },
          {
            element: (
              <RequirePermission permissions={[Permission.DEPOT_READ, Permission.LOADS_READ]} />
            ),
            children: [{ path: "/depot", element: <DepotDashboardPage /> }],
          },
          {
            element: (
              <RequirePermission
                permissions={[Permission.HAULIER_LOADS_READ, Permission.LOADS_READ]}
              />
            ),
            children: [{ path: "/haulier", element: <HaulierDashboardPage /> }],
          },
          {
            element: (
              <RequirePermission
                permissions={[
                  Permission.USERS_READ,
                  Permission.LOADS_READ,
                  Permission.SLOTS_APPROVE,
                  Permission.CUSTOMERS_READ,
                ]}
              />
            ),
            children: [{ path: "/admin", element: <SummaryPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.USERS_READ]} />,
            children: [{ path: "/admin/users", element: <UsersPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.GROUPS_READ]} />,
            children: [{ path: "/admin/groups", element: <GroupsPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.PERMISSIONS_READ]} />,
            children: [{ path: "/admin/permissions", element: <PermissionsPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.CONTACTS_READ]} />,
            children: [{ path: "/admin/contacts", element: <ContactsPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.CUSTOMERS_READ]} />,
            children: [{ path: "/admin/customers", element: <CustomersPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.HAULIERS_READ]} />,
            children: [{ path: "/admin/hauliers", element: <HauliersPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.RATE_CARDS_READ]} />,
            children: [{ path: "/admin/rate-cards", element: <RateCardsPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.CSE_READ]} />,
            children: [{ path: "/admin/cse", element: <CsePage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.SITES_READ]} />,
            children: [{ path: "/admin/sites", element: <SitesPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.BAYS_READ]} />,
            children: [{ path: "/admin/bays", element: <BaysPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.LOADPLANS_READ]} />,
            children: [{ path: "/admin/vehicle-types", element: <VehicleTypesPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.LOADS_READ]} />,
            children: [{ path: "/admin/loads", element: <LoadsPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.LOADS_READ]} />,
            children: [{ path: "/admin/collections", element: <CollectionsPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.LOADS_READ]} />,
            children: [{ path: "/admin/route-planner", element: <RoutePlannerPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.SLOTS_READ]} />,
            children: [
              { path: "/admin/scheduler", element: <LoadPlannerPage /> },
              { path: "/admin/planner", element: <Navigate to="/admin/scheduler" replace /> },
              { path: "/admin/container-load-planner", element: <ContainerLoadPlannerPage /> },
            ],
          },
          {
            element: <RequirePermission permissions={[Permission.SETTINGS_READ]} />,
            children: [{ path: "/admin/settings", element: <SettingsPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.SLOTS_APPROVE]} />,
            children: [{ path: "/admin/slot-approvals", element: <SlotApprovalsPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.AUDIT_READ]} />,
            children: [{ path: "/admin/audit", element: <AuditTrailPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.REPORTS_READ]} />,
            children: [{ path: "/admin/reports", element: <ReportsPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.IMPORTS_READ]} />,
            children: [{ path: "/admin/imports", element: <ImportsPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.INVOICES_READ]} />,
            children: [{ path: "/admin/invoices", element: <InvoicesPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.NOTIFICATIONS_READ]} />,
            children: [{ path: "/admin/notifications", element: <NotificationsPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.NOTIFICATIONS_WRITE]} />,
            children: [
              {
                path: "/admin/notification-templates",
                element: <NotificationTemplatesPage />,
              },
            ],
          },
          {
            element: <RequirePermission permissions={[Permission.NOTIFICATIONS_WRITE]} />,
            children: [{ path: "/admin/notification-rules", element: <NotificationRulesPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.LANGUAGES_READ]} />,
            children: [{ path: "/admin/languages", element: <LanguagesPage /> }],
          },
          {
            element: <RequirePermission permissions={[Permission.TRANSLATIONS_READ]} />,
            children: [{ path: "/admin/translations", element: <TranslationsPage /> }],
          },
        ],
      },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);

